import { useState, useEffect, useCallback, useRef } from "react";
import { useIsAuthenticated, useMsal, AuthenticatedTemplate, UnauthenticatedTemplate } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import {
  AreaChart, Area, LineChart, Line, BarChart, Bar,
  PieChart, Pie, Cell, RadialBarChart, RadialBar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import LoginPage from './LoginPage';
import { tokenRequest } from './authConfig';

// ─── CONFIG ──────────────────────────────────────────────────
const API_BASE     = process.env.REACT_APP_API_URL || "http://localhost:3001";
const REFRESH_SECS = 300;

// ─── GLOBAL STYLES ───────────────────────────────────────────
const GlobalStyles = () => (
  <style>{`
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
    :root {
      --bg:      #00091f; --bg2: #000d2e; --card: #001040;
      --border:  rgba(11,204,249,0.12); --border2: rgba(11,204,249,0.25);
      --navy:    #001B71; --cyan: #0BCCF9; --cyan2: #2BB5C8;
      --pizarra: #404B68; --neutro: #7a8baa; --white: #e8f4ff;
      --green:   #00e5a0; --red: #ff4466; --orange: #ffaa00;
      --fh: 'Manrope', sans-serif; --fm: 'DM Mono', monospace;
    }
    html, body, #root { height: 100%; }
    body {
      background: var(--bg); color: var(--white);
      font-family: var(--fm); font-size: 14px;
    }
    ::-webkit-scrollbar { width: 4px; height: 4px; }
    ::-webkit-scrollbar-track { background: transparent; }
    ::-webkit-scrollbar-thumb { background: var(--border2); border-radius: 2px; }
    .portal {
      min-height: 100vh; background: var(--bg);
      background-image:
        radial-gradient(ellipse 80% 50% at 50% -10%, rgba(11,204,249,0.07) 0%, transparent 60%),
        radial-gradient(ellipse 40% 40% at 90% 90%, rgba(0,27,113,0.25) 0%, transparent 60%);
    }
    /* TOPBAR */
    .topbar {
      display: flex; align-items: center; justify-content: space-between;
      padding: 0 24px; height: 54px;
      background: rgba(0,16,64,0.97); border-bottom: 1px solid var(--border);
      backdrop-filter: blur(12px); position: sticky; top: 0; z-index: 100;
    }
    .topbar-left, .topbar-right { display: flex; align-items: center; gap: 14px; }
    .brand { font-family: var(--fh); font-size: 15px; font-weight: 700; letter-spacing: .5px; }
    .brand span { color: var(--cyan); }
    .sep { width: 1px; height: 22px; background: var(--border2); }
    .mtag {
      display: flex; align-items: center; gap: 7px;
      background: rgba(11,204,249,0.08); border: 1px solid var(--border2);
      border-radius: 6px; padding: 4px 11px; font-size: 12px; color: var(--cyan);
    }
    .mdot { width: 7px; height: 7px; border-radius: 50%; background: var(--green);
      box-shadow: 0 0 6px var(--green); animation: pulse 2s infinite; }
    .errordot { background: var(--red); box-shadow: 0 0 6px var(--red); }
    @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:.4} }
    .rfbadge { display: flex; align-items: center; gap: 6px; font-size: 11px; color: var(--neutro); }
    .rfdot { width: 6px; height: 6px; border-radius: 50%; animation: pulse 1.5s infinite; }
    .timebadge { font-size: 13px; color: var(--cyan); letter-spacing: 1px; }
    /* MAIN */
    .main { padding: 18px 24px; display: flex; flex-direction: column; gap: 16px; }
    /* KPI STRIP */
    .kstrip { display: grid; grid-template-columns: repeat(5, 1fr); gap: 12px; }
    .kcard {
      background: var(--card); border: 1px solid var(--border);
      border-radius: 12px; padding: 16px 18px; position: relative;
      overflow: hidden; transition: border-color .2s, transform .2s;
      cursor: default;
    }
    .kcard:hover { border-color: var(--border2); transform: translateY(-1px); }
    .kcard::before { content:''; position:absolute; top:0; left:0; right:0; height:2px; background: var(--ka, var(--cyan)); }
    .klabel { font-size: 10px; color: var(--neutro); text-transform: uppercase; letter-spacing: 1.5px; margin-bottom: 8px; }
    .kval { font-family: var(--fh); font-size: 30px; font-weight: 800; line-height: 1; }
    .kval.g { color: var(--green); } .kval.c { color: var(--cyan); } .kval.r { color: var(--red); }
    .ksub { font-size: 11px; color: var(--neutro); margin-top: 5px; }
    .ksub b { color: var(--white); }
    .ktrd { position: absolute; right: 14px; top: 14px; font-size: 16px; opacity: .35; }
    /* GRID */
    .g2r { display: grid; grid-template-columns: 1fr 300px; gap: 14px; }
    .g3  { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 14px; }
    .gcol { display: flex; flex-direction: column; gap: 14px; }
    /* PANELS */
    .panel {
      background: var(--card); border: 1px solid var(--border);
      border-radius: 12px; padding: 18px;
    }
    .ptitle {
      font-family: var(--fh); font-size: 12px; font-weight: 600;
      color: var(--neutro); text-transform: uppercase; letter-spacing: 1px;
      margin-bottom: 16px; display: flex; align-items: center; justify-content: space-between;
    }
    .pbadge {
      font-size: 10px; font-family: var(--fm); background: rgba(11,204,249,0.1);
      color: var(--cyan); border: 1px solid var(--border2); border-radius: 4px;
      padding: 2px 7px; text-transform: none; letter-spacing: 0;
    }
    /* TOOLTIP */
    .ctt { background: rgba(0,16,64,0.96); border: 1px solid var(--border2);
      border-radius: 8px; padding: 9px 13px; font-size: 11px; }
    .ctt .cl { color: var(--neutro); margin-bottom: 3px; }
    .ctt .cv { color: var(--white); font-weight: 500; }
    /* DONUT */
    .dlegend { display: flex; flex-direction: column; gap: 7px; margin-top: 12px; }
    .ditem { display: flex; align-items: center; justify-content: space-between; font-size: 11px; }
    .ddot { width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0; }
    .dname { color: var(--neutro); flex: 1; margin-left: 7px; }
    .dval { color: var(--white); font-weight: 500; }
    .dpct { color: var(--cyan); margin-left: 7px; width: 32px; text-align: right; }
    /* GAUGE */
    .gwrap { display: flex; align-items: center; justify-content: center; position: relative; }
    .glabel { position: absolute; text-align: center; font-family: var(--fh); top: 55%; transform: translateY(-50%); }
    .gnum { font-size: 24px; font-weight: 800; line-height: 1; }
    .gunit { font-size: 10px; color: var(--neutro); margin-top: 3px; }
    /* TXN FEED */
    .thdr {
      display: grid; grid-template-columns: 42px 56px 76px 44px 76px 1fr 56px;
      gap: 6px; padding: 5px 10px; font-size: 9px; color: var(--pizarra);
      text-transform: uppercase; letter-spacing: 1px; margin-bottom: 3px;
    }
    .tfeed { display: flex; flex-direction: column; max-height: 320px; overflow-y: auto; }
    .trow {
      display: grid; grid-template-columns: 42px 56px 76px 44px 76px 1fr 56px;
      align-items: center; gap: 6px; padding: 8px 10px; font-size: 11px;
      border-bottom: 1px solid rgba(11,204,249,0.05); border-radius: 5px;
      transition: background .15s;
    }
    .trow:hover { background: rgba(11,204,249,0.04); }
    .trow.new { animation: slIn .4s ease; }
    @keyframes slIn { from{opacity:0;transform:translateX(-5px)} to{opacity:1;transform:none} }
    .tt { color: var(--neutro); }
    .ta { color: var(--white); font-weight: 500; text-align: right; }
    .tc,.te { color: var(--neutro); font-size: 10px; }
    .tterm { color: var(--pizarra); font-size: 10px; text-align: center; }
    .tauth { color: var(--neutro); font-size: 10px; }
    .chip {
      display: inline-flex; align-items: center; padding: 2px 7px;
      border-radius: 4px; font-size: 9px; font-weight: 700;
      text-transform: uppercase; letter-spacing: .5px; white-space: nowrap;
    }
    .chip.auth    { background: rgba(0,229,160,.12); color: var(--green); }
    .chip.dec     { background: rgba(255,68,102,.12); color: var(--red); }
    .chip.decterm { background: rgba(255,68,102,.12); color: var(--red); }
    .chip.err     { background: rgba(255,170,0,.12);  color: var(--orange); }
    .chip.unknown { background: rgba(122,139,170,.1); color: var(--neutro); }
    /* ALERTS */
    .alist { display: flex; flex-direction: column; gap: 7px; }
    .aitem {
      display: flex; align-items: flex-start; gap: 9px;
      padding: 9px 11px; border-radius: 7px; font-size: 11px; border: 1px solid;
    }
    .aitem.warn  { background: rgba(255,170,0,.06); border-color: rgba(255,170,0,.2); }
    .aitem.error { background: rgba(255,68,102,.06); border-color: rgba(255,68,102,.2); }
    .aitem.info  { background: rgba(11,204,249,.06); border-color: rgba(11,204,249,.15); }
    .atime { color: var(--neutro); font-size: 10px; margin-top: 2px; }
    .amsg  { color: var(--white); line-height: 1.4; }
    /* TERMINAL */
    .titem { margin-bottom: 14px; }
    .thdr2 { display: flex; justify-content: space-between; font-size: 11px; margin-bottom: 5px; }
    .tname { color: var(--white); font-weight: 500; }
    .trate { color: var(--cyan); font-family: var(--fh); font-weight: 700; }
    .btrack { height: 5px; background: rgba(11,204,249,.1); border-radius: 3px; overflow: hidden; }
    .bfill { height: 100%; border-radius: 3px;
      background: linear-gradient(90deg, var(--cyan2), var(--cyan)); transition: width 1s ease; }
    .tstats { display: flex; gap: 14px; margin-top: 5px; font-size: 10px; color: var(--neutro); }
    .tstats b { color: var(--white); }
    /* LOADING */
    .loading { display: flex; align-items: center; justify-content: center;
      height: 180px; color: var(--neutro); font-size: 12px; gap: 8px; }
    .ldot { width: 5px; height: 5px; border-radius: 50%; background: var(--cyan);
      animation: ldot 1.2s infinite; }
    .ldot:nth-child(2){animation-delay:.2s} .ldot:nth-child(3){animation-delay:.4s}
    @keyframes ldot { 0%,80%,100%{opacity:.2} 40%{opacity:1} }
    /* ERROR */
    .ebanner {
      background: rgba(255,68,102,.08); border: 1px solid rgba(255,68,102,.3);
      border-radius: 8px; padding: 11px 15px; color: var(--red); font-size: 12px;
      display: flex; align-items: center; gap: 10px;
    }
    .ebanner button {
      margin-left: auto; background: transparent; border: 1px solid var(--red);
      color: var(--red); border-radius: 4px; padding: 3px 9px;
      cursor: pointer; font-size: 11px; font-family: var(--fm);
    }
    .ebanner button:hover { background: rgba(255,68,102,.1); }
    /* FOOTER */
    .footer {
      text-align: center; padding: 16px 24px; font-size: 10px;
      color: var(--pizarra); border-top: 1px solid var(--border);
    }
    .footer span { color: var(--cyan); }
    /* RESPONSIVE */
    @media (max-width: 1100px) {
      .kstrip { grid-template-columns: repeat(3, 1fr); }
      .g2r { grid-template-columns: 1fr; }
      .g3 { grid-template-columns: 1fr 1fr; }
    }
    @media (max-width: 700px) {
      .kstrip { grid-template-columns: 1fr 1fr; }
      .g3 { grid-template-columns: 1fr; }
      .thdr, .trow { grid-template-columns: 42px 60px 70px 1fr 56px; }
      .thdr > *:nth-child(4),
      .trow > *:nth-child(4) { display: none; }
    }
  `}</style>
);

// ─── HELPERS ─────────────────────────────────────────────────
const CT = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="ctt">
      <div className="cl">{label}</div>
      {payload.map((p, i) => (
        <div key={i} className="cv" style={{ color: p.color }}>
          {p.name}: {typeof p.value === "number" ? p.value.toLocaleString() : p.value}
          {p.name === "Rate" ? "%" : ""}
        </div>
      ))}
    </div>
  );
};

const Chip = ({ status }) => {
  const map = { AUTH:"auth", DEC:"dec", "DEC-TERM":"decterm", ERR:"err" };
  const lbl = { AUTH:"Auth", DEC:"Dec", "DEC-TERM":"Dec↩", ERR:"Err" };
  return <span className={`chip ${map[status]||"unknown"}`}>{lbl[status]||status}</span>;
};

const Donut = ({ data, title, badge }) => (
  <div className="panel">
    <div className="ptitle">{title}<span className="pbadge">{badge}</span></div>
    <ResponsiveContainer width="100%" height={130}>
      <PieChart>
        <Pie data={data} cx="50%" cy="50%" innerRadius={38} outerRadius={58}
          dataKey="value" paddingAngle={2} startAngle={90} endAngle={-270}>
          {data.map((e, i) => <Cell key={i} fill={e.color} strokeWidth={0} />)}
        </Pie>
        <Tooltip content={<CT />} />
      </PieChart>
    </ResponsiveContainer>
    <div className="dlegend">
      {data.filter(d => d.count > 0).map((d, i) => (
        <div className="ditem" key={i}>
          <div className="ddot" style={{ background: d.color }} />
          <span className="dname">{d.name}</span>
          <span className="dval">{d.count}</span>
          <span className="dpct">{d.value}%</span>
        </div>
      ))}
    </div>
  </div>
);

const Loading = () => (
  <div className="loading">
    <div className="ldot"/><div className="ldot"/><div className="ldot"/>
    <span>Querying Azure Log Analytics...</span>
  </div>
);

// ─── APP ─────────────────────────────────────────────────────
export default function App() {
  return (
    <>
      <AuthenticatedTemplate>
        <Dashboard />
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <LoginPage />
      </UnauthenticatedTemplate>
    </>
  );
}

function Dashboard() {
  const { instance, accounts } = useMsal();
  const account   = accounts[0];
  const userName  = account?.name || account?.username || 'Usuario';

  const [data, setData]             = useState(null);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [countdown, setCountdown]   = useState(REFRESH_SECS);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [now, setNow]               = useState(new Date());
  const timerRef                    = useRef(null);

  // Live clock
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // Get auth token silently
  const getToken = useCallback(async () => {
    if (!account) return null;
    try {
      const r = await instance.acquireTokenSilent({ ...tokenRequest, account });
      return r.accessToken;
    } catch {
      instance.acquireTokenRedirect({ ...tokenRequest, account });
      return null;
    }
  }, [instance, account]);

  // Fetch from API with Bearer token
  const fetchData = useCallback(async () => {
    setError(null);
    try {
      const token = await getToken();
      if (!token) return;
      const mxNow = new Date(Date.now() - 6 * 3600000);
      const today = mxNow.toISOString().slice(0, 10);
      const res   = await fetch(`${API_BASE}/api/dashboard?date=${today}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
      const json  = await res.json();
      if (json.error) throw new Error(json.error);
      setData(json);
      setLastRefresh(new Date());
      setCountdown(REFRESH_SECS);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    fetchData();
    timerRef.current = setInterval(fetchData, REFRESH_SECS * 1000);
    return () => clearInterval(timerRef.current);
  }, [fetchData]);

  useEffect(() => {
    const t = setInterval(() => setCountdown(c => c > 0 ? c - 1 : REFRESH_SECS), 1000);
    return () => clearInterval(t);
  }, []);

  // Mexico time
  const mxTime = new Date(now.getTime() - 6 * 3600000);
  const timeStr = mxTime.toLocaleTimeString("es-MX", { hour:"2-digit", minute:"2-digit", second:"2-digit" });
  const dateStr = mxTime.toLocaleDateString("es-MX", { weekday:"long", day:"numeric", month:"long" });

  // Data shortcuts
  const kpi       = data?.kpi         || {};
  const hourly    = data?.hourly      || [];
  const txns      = data?.transactions || [];
  const cards     = data?.cards       || [];
  const entries   = data?.entries     || [];
  const terminals = data?.terminals   || [];
  const alerts    = data?.alerts      || [];
  const ac        = data?.alertCounts || {};
  const merchant  = data?.merchant    || {};

  const authColor = kpi.authRate >= 95 ? "var(--green)" : kpi.authRate >= 88 ? "var(--cyan)" : "var(--red)";

  return (
    <>
      <GlobalStyles />
      <div className="portal">

        {/* ── TOPBAR ─────────────────────────────────────────── */}
        <header className="topbar">
          <div className="topbar-left">
            <div className="brand">CUSTOS <span>XIIBERO</span></div>
            <div className="sep" />
            <div className="mtag">
              <div className={`mdot ${error ? "errordot" : ""}`} />
              {merchant.name || "Loading..."} · {merchant.currency || "MXN"} · {merchant.processor || "CBS"}
            </div>
            <div className="sep" />
            <div style={{ fontSize:11, color:"var(--neutro)", display:"flex", gap:8 }}>
              {terminals.map(t => (
                <span key={t.id}>
                  <span style={{ color:"var(--cyan)" }}>T-{t.id}</span>
                  <span style={{ color: t.rate >= 90 ? "var(--green)" : "var(--orange)" }}> {t.rate}%</span>
                </span>
              ))}
            </div>
          </div>
          <div className="topbar-right">
            {(ac.error > 0) && (
              <div style={{ fontSize:11, color:"var(--red)", display:"flex", alignItems:"center", gap:5 }}>
                🔴 {ac.error} error{ac.error > 1 ? "s" : ""}
              </div>
            )}
            <div className="rfbadge">
              <div className="rfdot" style={{ background: loading ? "var(--orange)" : error ? "var(--red)" : "var(--cyan)" }} />
              {loading ? "Updating..." : error ? "Error" : `Refresh in ${Math.floor(countdown/60)}:${String(countdown%60).padStart(2,"0")}`}
            </div>
            <div className="sep" />
            <div className="timebadge">
              {timeStr} <span style={{ fontSize:10, color:"var(--neutro)" }}>CST</span>
            </div>
            <div className="sep" />
            <div style={{ display:"flex", alignItems:"center", gap:8 }}>
              <div style={{ fontSize:11, color:"var(--neutro)" }}>👤 {userName}</div>
              <button
                onClick={() => instance.logoutRedirect()}
                style={{ background:"transparent", border:"1px solid var(--border2)",
                  color:"var(--neutro)", borderRadius:4, padding:"3px 9px",
                  cursor:"pointer", fontSize:11, fontFamily:"var(--fm)" }}>
                Salir
              </button>
            </div>
          </div>
        </header>

        <main className="main">

          {/* ── ERROR BANNER ───────────────────────────────────── */}
          {error && (
            <div className="ebanner">
              🔴 <span><b>Error connecting to API:</b> {error}</span>
              <button onClick={fetchData}>Retry</button>
            </div>
          )}

          {/* ── DATE HEADER ────────────────────────────────────── */}
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-end" }}>
            <div>
              <div style={{ fontFamily:"var(--fh)", fontSize:20, fontWeight:800, textTransform:"capitalize" }}>
                {dateStr}
              </div>
              <div style={{ fontSize:11, color:"var(--neutro)", marginTop:3 }}>
                Merchant Portal · Real-time Performance · Mexico Central Time (CST UTC-6)
                {lastRefresh && (
                  <span style={{ marginLeft:10, color:"var(--pizarra)" }}>
                    · Updated {lastRefresh.toLocaleTimeString("es-MX")}
                  </span>
                )}
              </div>
            </div>
            <div style={{ textAlign:"right" }}>
              <div style={{ fontSize:10, color:"var(--neutro)", textTransform:"uppercase", letterSpacing:1 }}>MID</div>
              <div style={{ fontSize:12, color:"var(--cyan)" }}>{merchant.mid || "—"}</div>
            </div>
          </div>

          {/* ── KPI STRIP ──────────────────────────────────────── */}
          {loading && !data ? <Loading /> : (
            <>
              <div className="kstrip">
                <div className="kcard" style={{"--ka":"var(--green)"}}>
                  <div className="ktrd">✓</div>
                  <div className="klabel">Authorized</div>
                  <div className="kval g">{kpi.totalAuth ?? "—"}</div>
                  <div className="ksub">transactions today</div>
                </div>
                <div className="kcard" style={{"--ka":"var(--cyan)"}}>
                  <div className="ktrd">₱</div>
                  <div className="klabel">Total Amount</div>
                  <div className="kval c">
                    {kpi.totalAmount ? `${(kpi.totalAmount/1000).toFixed(1)}K` : "—"}
                  </div>
                  <div className="ksub">MXN authorized</div>
                </div>
                <div className="kcard" style={{"--ka": kpi.authRate >= 90 ? "var(--green)" : "var(--orange)"}}>
                  <div className="ktrd">%</div>
                  <div className="klabel">Auth Rate</div>
                  <div className={`kval ${kpi.authRate >= 90 ? "g" : "c"}`}>{kpi.authRate ?? "—"}%</div>
                  <div className="ksub"><b>{kpi.totalAuth}</b> of <b>{kpi.totalAttempts}</b> attempts</div>
                </div>
                <div className="kcard" style={{"--ka":"var(--cyan2)"}}>
                  <div className="ktrd">⇄</div>
                  <div className="klabel">Terminals</div>
                  <div className="kval c">{terminals.length}</div>
                  <div className="ksub">{terminals.map(t => `T-${t.id}`).join(" · ")}</div>
                </div>
                <div className="kcard" style={{"--ka": (ac.error||0)+(ac.warn||0) > 0 ? "var(--orange)" : "var(--green)"}}>
                  <div className="ktrd">⚠</div>
                  <div className="klabel">Alerts</div>
                  <div className={`kval ${(ac.error||0) > 0 ? "r" : (ac.warn||0) > 0 ? "" : "g"}`}
                    style={(ac.warn||0) > 0 && (ac.error||0) === 0 ? {color:"var(--orange)"} : {}}>
                    {(ac.error||0) + (ac.warn||0)}
                  </div>
                  <div className="ksub">
                    {(ac.error||0) === 0 && (ac.warn||0) === 0
                      ? "All clear"
                      : `${ac.error||0} err · ${ac.warn||0} warn`}
                  </div>
                </div>
              </div>

              {/* ── ROW 2: Chart + Right panel ─────────────────── */}
              <div className="g2r">

                {/* Hourly Volume Chart */}
                <div className="panel">
                  <div className="ptitle">
                    Hourly Volume
                    <span className="pbadge">CST (UTC-6)</span>
                  </div>
                  {hourly.length === 0 ? <Loading /> : (
                    <>
                      <ResponsiveContainer width="100%" height={190}>
                        <AreaChart data={hourly} margin={{top:4,right:4,bottom:0,left:-20}}>
                          <defs>
                            <linearGradient id="ag" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%"  stopColor="#0BCCF9" stopOpacity={.25}/>
                              <stop offset="95%" stopColor="#0BCCF9" stopOpacity={0}/>
                            </linearGradient>
                            <linearGradient id="dg" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%"  stopColor="#ff4466" stopOpacity={.2}/>
                              <stop offset="95%" stopColor="#ff4466" stopOpacity={0}/>
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="2 4" stroke="rgba(11,204,249,0.07)" vertical={false}/>
                          <XAxis dataKey="hour" tick={{fill:"#7a8baa",fontSize:11,fontFamily:"DM Mono"}} tickLine={false} axisLine={false}/>
                          <YAxis tick={{fill:"#7a8baa",fontSize:10,fontFamily:"DM Mono"}} tickLine={false} axisLine={false}/>
                          <Tooltip content={<CT/>}/>
                          <Area type="monotone" dataKey="auth" name="Authorized" stroke="#0BCCF9" strokeWidth={2} fill="url(#ag)" dot={false} activeDot={{r:4}}/>
                          <Area type="monotone" dataKey="dec"  name="Declined"   stroke="#ff4466" strokeWidth={1.5} fill="url(#dg)" dot={false} activeDot={{r:3}}/>
                        </AreaChart>
                      </ResponsiveContainer>
                      <div style={{marginTop:8}}>
                        <div style={{fontSize:9,color:"var(--neutro)",textTransform:"uppercase",letterSpacing:1,marginBottom:4}}>
                          Auth Rate % / hour
                        </div>
                        <ResponsiveContainer width="100%" height={44}>
                          <LineChart data={hourly} margin={{top:2,right:4,bottom:0,left:-20}}>
                            <XAxis dataKey="hour" hide/>
                            <YAxis domain={[70,105]} hide/>
                            <Tooltip content={<CT/>}/>
                            <Line type="monotone" dataKey="rate" name="Rate" stroke="#2BB5C8" strokeWidth={1.5} dot={false} activeDot={{r:3}}/>
                            <CartesianGrid strokeDasharray="2 4" stroke="rgba(11,204,249,0.05)" vertical={false}/>
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </>
                  )}
                </div>

                {/* Right column: Gauge + Alerts */}
                <div className="gcol">
                  <div className="panel">
                    <div className="ptitle">Authorization Rate</div>
                    <div style={{position:"relative",height:130}}>
                      <svg viewBox="0 0 200 110" width="100%" height="130">
                        {/* Background arc */}
                        <path d="M 20 100 A 80 80 0 0 1 180 100" fill="none" stroke="rgba(11,204,249,0.1)" strokeWidth="16" strokeLinecap="round"/>
                        {/* Foreground arc — clipped by authRate */}
                        {kpi.authRate != null && (
                          <path
                            d={(() => {
                              const pct = Math.min(kpi.authRate, 100) / 100;
                              const angle = Math.PI * pct; // 0..π
                              const x = 100 - 80 * Math.cos(angle);
                              const y = 100 - 80 * Math.sin(angle);
                              const large = pct > 0.5 ? 1 : 0;
                              return `M 20 100 A 80 80 0 ${large} 1 ${x.toFixed(2)} ${y.toFixed(2)}`;
                            })()}
                            fill="none" stroke={authColor} strokeWidth="16" strokeLinecap="round"
                          />
                        )}
                        {/* Label */}
                        <text x="100" y="72" textAnchor="middle" dominantBaseline="middle"
                          fill={authColor} fontSize="22" fontWeight="800" fontFamily="Manrope, sans-serif">
                          {kpi.authRate ?? "—"}%
                        </text>
                        <text x="100" y="94" textAnchor="middle" dominantBaseline="middle"
                          fill="#7a8baa" fontSize="11" fontFamily="DM Mono, monospace">
                          Auth Rate
                        </text>
                      </svg>
                    </div>
                    <div style={{marginTop:8,display:"flex",justifyContent:"space-around",fontSize:11}}>
                      {terminals.map((t,i) => (
                        <div key={t.id} style={{textAlign:"center"}}>
                          <div style={{color:"var(--neutro)"}}>T-{t.id}</div>
                          <div style={{color:t.rate>=90?"var(--green)":"var(--orange)",fontWeight:700}}>{t.rate}%</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="panel" style={{flex:1}}>
                    <div className="ptitle">
                      Alerts
                      {(ac.error||0)+(ac.warn||0) > 0 ? (
                        <span className="pbadge" style={{color:"var(--orange)",borderColor:"rgba(255,170,0,.3)",background:"rgba(255,170,0,.08)"}}>
                          {(ac.error||0)+(ac.warn||0)} active
                        </span>
                      ) : (
                        <span className="pbadge" style={{color:"var(--green)",borderColor:"rgba(0,229,160,.3)",background:"rgba(0,229,160,.08)"}}>
                          All clear
                        </span>
                      )}
                    </div>
                    <div className="alist">
                      {alerts.length === 0 ? (
                        <div style={{fontSize:11,color:"var(--neutro)",textAlign:"center",padding:"14px 0"}}>
                          ✅ No anomalies detected today
                        </div>
                      ) : alerts.slice(0,5).map((a,i) => (
                        <div className={`aitem ${a.type}`} key={i}>
                          <div style={{fontSize:13,flexShrink:0}}>{a.icon}</div>
                          <div>
                            <div className="amsg">{a.msg}</div>
                            <div className="atime">{a.time} CST</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* ── ROW 3: Donuts + Terminals ──────────────────── */}
              <div className="g3">
                <Donut data={cards}   title="Card Brands"      badge="Authorized" />
                <Donut data={entries} title="Entry Technology" badge="Authorized" />

                <div className="panel">
                  <div className="ptitle">Terminal Performance <span className="pbadge">Live</span></div>
                  {terminals.map(t => (
                    <div className="titem" key={t.id}>
                      <div className="thdr2">
                        <span className="tname">{t.label}</span>
                        <span className="trate">{t.rate}%</span>
                      </div>
                      <div className="btrack">
                        <div className="bfill" style={{width:`${t.rate}%`}}/>
                      </div>
                      <div className="tstats">
                        <span>Auth <b style={{color:"var(--green)"}}>{t.auth}</b></span>
                        <span>Dec <b style={{color:"var(--red)"}}>{t.dec}</b></span>
                        <span>MXN <b>{(t.amount/1000).toFixed(1)}K</b></span>
                      </div>
                    </div>
                  ))}
                  {terminals.length > 1 && (
                    <div style={{marginTop:12,paddingTop:12,borderTop:"1px solid var(--border)"}}>
                      <div style={{fontSize:9,color:"var(--neutro)",textTransform:"uppercase",letterSpacing:1,marginBottom:8}}>
                        Amount by Terminal (MXN)
                      </div>
                      <ResponsiveContainer width="100%" height={55}>
                        <BarChart data={terminals} layout="vertical" margin={{left:0,right:0}}>
                          <XAxis type="number" hide/>
                          <YAxis type="category" dataKey="id" hide/>
                          <Tooltip content={<CT/>}/>
                          <Bar dataKey="amount" name="MXN" radius={[0,4,4,0]}>
                            {terminals.map((t,i) => <Cell key={i} fill={i===0?"#0BCCF9":"#001B71"}/>)}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </div>
              </div>

              {/* ── ROW 4: Live Feed ───────────────────────────── */}
              <div className="panel">
                <div className="ptitle">
                  Live Transaction Feed
                  <div style={{display:"flex",alignItems:"center",gap:10}}>
                    <div style={{display:"flex",gap:12,fontSize:10}}>
                      <span style={{color:"var(--green)"}}>● AUTH</span>
                      <span style={{color:"var(--red)"}}>● DEC</span>
                      <span style={{color:"var(--orange)"}}>● ERR</span>
                    </div>
                    <span className="pbadge">{txns.length} txns · auto-refresh 5 min</span>
                  </div>
                </div>
                <div className="thdr">
                  <div>Time</div><div style={{textAlign:"right"}}>Amount</div>
                  <div>Status</div><div>Card</div><div>Entry</div>
                  <div>Auth Code</div><div style={{textAlign:"center"}}>Term.</div>
                </div>
                <div className="tfeed">
                  {txns.length === 0 ? (
                    <div style={{fontSize:11,color:"var(--neutro)",textAlign:"center",padding:"20px 0"}}>
                      No transactions found for this period
                    </div>
                  ) : txns.map((t,i) => (
                    <div className={`trow ${i===0?"new":""}`} key={t.id+i}>
                      <div className="tt">{t.time}</div>
                      <div className="ta">{t.amount}</div>
                      <div><Chip status={t.status}/></div>
                      <div className="tc">{t.card}</div>
                      <div className="te">{t.entry}</div>
                      <div className="tauth" style={{fontFamily:"var(--fm)"}}>{t.auth||"—"}</div>
                      <div className="tterm">T-{t.terminal}</div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </main>

        <footer className="footer">
          CUSTOS <span>XIIBERO</span> Payment Platform · Merchant Portal v1.0 ·
          Data: Azure Log Analytics · Refresh: <span>5 min</span> ·
          Times: Mexico Central <span>(CST UTC-6)</span>
          {lastRefresh && <> · Updated: <span>{lastRefresh.toLocaleTimeString("es-MX")}</span></>}
        </footer>

      </div>
    </>
  );
}
